import React, { useState } from 'react';
import { MessageSquareWarning, X, CheckCircle2 } from 'lucide-react';

// Issue types must match ROADMAP_ISSUE_TYPES in
// functions/lib/roadmapFeedbackValidation.js (the server re-validates).
const ISSUE_TYPES = [
  'Not easy to use',
  'Link not working',
  'Info incorrect',
  'Not relevant',
  'Something missing',
  'Other',
];
const DETAILS_MAX = 2000;

// submitRoadmapFeedback is an onRequest endpoint (NOT an onCall) so the same
// endpoint can be reached both here and from the SDK-less static guide pages
// (gate.js). Both POST the same JSON. europe-west2 to match the project region.
const FEEDBACK_URL =
  'https://europe-west2-arc-tracker-40885.cloudfunctions.net/submitRoadmapFeedback';

/**
 * Floating "Report a problem" widget shown on every Roadmap surface (gate,
 * pricing, tool, subscription preview). A failed send never breaks the page —
 * it only shows a gentle retry message.
 */
export default function RoadmapFeedbackWidget({
  surface = 'roadmap',
  pagePath,
  pageContext = '',
  email = '',
}) {
  const [open, setOpen] = useState(false);
  const [issueType, setIssueType] = useState('');
  const [details, setDetails] = useState('');
  const [optEmail, setOptEmail] = useState('');
  const [hp, setHp] = useState(''); // honeypot — must stay empty for humans
  const [state, setState] = useState('idle'); // idle | sending | done | error
  const [err, setErr] = useState('');

  const knownEmail = (email || '').trim();
  const path =
    pagePath || (typeof window !== 'undefined' ? window.location.pathname : '');

  const reset = () => {
    setIssueType(''); setDetails(''); setOptEmail(''); setHp('');
    setState('idle'); setErr('');
  };
  const close = () => { setOpen(false); setTimeout(reset, 200); };

  const submit = async () => {
    if (!issueType) { setErr('Please choose what went wrong.'); return; }
    if (!details.trim()) { setErr('Please add a little detail.'); return; }
    setState('sending'); setErr('');
    try {
      const res = await fetch(FEEDBACK_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          issueType,
          details: details.trim().slice(0, DETAILS_MAX),
          email: knownEmail || optEmail.trim(),
          pagePath: path,
          pageContext,
          surface,
          hp,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) throw new Error(data.error || 'send failed');
      setState('done');
    } catch (e) {
      // Never break the page on a failed send.
      setState('error');
      setErr("Sorry, that didn't send. Please try again in a moment.");
    }
  };

  return (
    <>
      {/* Floating trigger */}
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Report a problem"
          className="fixed bottom-4 right-4 z-[60] flex items-center gap-2 rounded-full border border-gold/40 bg-ink/95 px-3.5 py-2.5 text-xs font-semibold text-gold shadow-lg backdrop-blur transition hover:bg-ink hover:border-gold sm:bottom-5 sm:right-5"
        >
          <MessageSquareWarning className="h-4 w-4" />
          <span className="hidden sm:inline">Report a problem</span>
        </button>
      )}

      {/* Panel */}
      {open && (
        <div className="fixed bottom-4 right-4 left-4 z-[60] mx-auto max-w-sm rounded-2xl border border-gold/30 bg-ink text-parchment shadow-2xl sm:left-auto sm:bottom-5 sm:right-5">
          <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
            <div className="flex items-center gap-2">
              <MessageSquareWarning className="h-4 w-4 text-gold" />
              <span className="text-sm font-semibold text-parchment">Report a problem</span>
            </div>
            <button
              type="button"
              onClick={close}
              aria-label="Close"
              className="rounded-md p-1 text-parchment/60 hover:bg-white/10 hover:text-parchment"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {state === 'done' ? (
            <div className="px-4 py-8 text-center">
              <CheckCircle2 className="mx-auto mb-3 h-8 w-8 text-gold" />
              <p className="text-sm font-semibold text-parchment">Thank you — that's noted.</p>
              <p className="mt-1 text-xs text-parchment/60">Your report helps us fix the roadmap during the beta.</p>
              <button
                type="button"
                onClick={close}
                className="mt-5 rounded-lg bg-gradient-to-r from-gold to-gold-deep px-4 py-2 text-xs font-bold text-ink hover:from-gold-light hover:to-gold"
              >
                Done
              </button>
            </div>
          ) : (
            <div className="max-h-[70vh] overflow-y-auto px-4 py-4">
              {/* Issue type */}
              <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-widest text-parchment/70">
                What went wrong? <span className="text-gold">*</span>
              </label>
              <div className="mb-3 grid grid-cols-2 gap-1.5">
                {ISSUE_TYPES.map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setIssueType(t)}
                    className={`rounded-lg border px-2 py-1.5 text-left text-xs transition ${
                      issueType === t
                        ? 'border-gold bg-gold/15 text-gold'
                        : 'border-white/10 text-parchment/70 hover:border-white/25'
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>

              {/* Details */}
              <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-widest text-parchment/70">
                Details <span className="text-gold">*</span>
              </label>
              <textarea
                value={details}
                maxLength={DETAILS_MAX}
                onChange={(e) => setDetails(e.target.value)}
                rows={3}
                placeholder="Tell us what happened…"
                className="w-full resize-none rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm text-parchment placeholder:text-parchment/60 focus:border-gold focus:outline-none"
              />
              <div className="mt-1 text-right text-[10px] text-parchment/60">
                {details.length}/{DETAILS_MAX}
              </div>

              {/* Reporter email */}
              {knownEmail ? (
                <p className="mt-1 text-xs text-parchment/60">
                  Reporting as <span className="text-gold">{knownEmail}</span>
                </p>
              ) : (
                <div className="mt-1">
                  <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-widest text-parchment/70">
                    Your email <span className="font-normal normal-case tracking-normal text-parchment/60">(optional)</span>
                  </label>
                  <input
                    type="email"
                    value={optEmail}
                    onChange={(e) => setOptEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="w-full rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm text-parchment placeholder:text-parchment/60 focus:border-gold focus:outline-none"
                  />
                </div>
              )}

              {/* Auto-captured context (shown to the user) */}
              <p className="mt-3 rounded-lg bg-white/5 px-2.5 py-1.5 text-[10px] leading-relaxed text-parchment/60">
                Sent with: <span className="text-parchment/80">{pageContext || path}</span>
                <span className="text-parchment/50"> · {path}</span>
              </p>

              {/* Honeypot — visually hidden, off-screen, not a real field for humans */}
              <input
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={hp}
                onChange={(e) => setHp(e.target.value)}
                aria-hidden="true"
                style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }}
              />

              {err && <p className="mt-2 text-xs text-rose-300">{err}</p>}

              <button
                type="button"
                onClick={submit}
                disabled={state === 'sending'}
                className="mt-3 w-full rounded-lg bg-gradient-to-r from-gold to-gold-deep px-4 py-2.5 text-sm font-bold text-ink transition hover:from-gold-light hover:to-gold disabled:opacity-60"
              >
                {state === 'sending' ? 'Sending…' : 'Send report'}
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );
}
